import React from 'react';
import { motion } from 'framer-motion';
import { SectionHeader } from '../components/SectionHeader';
import { Button } from '../components/Button';
import { GlowOrb } from '../components/GlowOrb';
import { MessageCircle, Star, Moon, Sun, CheckCircle2 } from 'lucide-react';
export function Astrology() {
  const zodiacSigns = [{
    sign: 'Aries',
    dates: 'Mar 21 - Apr 19',
    element: 'Fire',
    symbol: '♈'
  }, {
    sign: 'Taurus',
    dates: 'Apr 20 - May 20',
    element: 'Earth',
    symbol: '♉'
  }, {
    sign: 'Gemini',
    dates: 'May 21 - Jun 20',
    element: 'Air',
    symbol: '♊'
  }, {
    sign: 'Cancer',
    dates: 'Jun 21 - Jul 22',
    element: 'Water',
    symbol: '♋'
  }, {
    sign: 'Leo',
    dates: 'Jul 23 - Aug 22',
    element: 'Fire',
    symbol: '♌'
  }, {
    sign: 'Virgo',
    dates: 'Aug 23 - Sep 22',
    element: 'Earth',
    symbol: '♍'
  }, {
    sign: 'Libra',
    dates: 'Sep 23 - Oct 22',
    element: 'Air',
    symbol: '♎'
  }, {
    sign: 'Scorpio',
    dates: 'Oct 23 - Nov 21',
    element: 'Water',
    symbol: '♏'
  }, {
    sign: 'Sagittarius',
    dates: 'Nov 22 - Dec 21',
    element: 'Fire',
    symbol: '♐'
  }, {
    sign: 'Capricorn',
    dates: 'Dec 22 - Jan 19',
    element: 'Earth',
    symbol: '♑'
  }, {
    sign: 'Aquarius',
    dates: 'Jan 20 - Feb 18',
    element: 'Air',
    symbol: '♒'
  }, {
    sign: 'Pisces',
    dates: 'Feb 19 - Mar 20',
    element: 'Water',
    symbol: '♓'
  }];
  return <div className="pb-24 overflow-hidden" data-id="element-259">
      {/* Hero */}
      <section className="pt-32 pb-32 relative min-h-[90vh] flex items-center" data-id="element-260">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1534447677768-be436bb09401?q=80&w=2000&auto=format&fit=crop')] bg-cover bg-center opacity-40" data-id="element-261"></div>
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1543722530-d2c3201371e7?q=80&w=2000&auto=format&fit=crop')] bg-cover bg-center opacity-30 mix-blend-screen" data-id="element-262"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-midnight/90 via-midnight/40 to-midnight" data-id="element-263"></div>
        <GlowOrb color="gold" size="xl" position="top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-264" />

        <div className="container mx-auto px-6 relative z-10 text-center" data-id="element-265">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1
        }} className="max-w-4xl mx-auto" data-id="element-266">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gold/30 bg-gold/10 backdrop-blur-md mb-8" data-id="element-267">
              <Star className="w-4 h-4 text-gold" data-id="element-268" />
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-gold" data-id="element-269">
                Cosmic Insight
              </span>
              <Star className="w-4 h-4 text-gold" data-id="element-270" />
            </div>
            <h1 className="text-5xl md:text-7xl lg:text-[5.5rem] font-serif text-white mb-8 leading-tight" data-id="element-271">
              Unlock Your Future with Free Astrology Guidance
            </h1>
            <p className="text-xl text-beige-muted mb-12 max-w-2xl mx-auto font-light" data-id="element-272">
              Discover your love, career, and life path through personalized
              astrology insights — completely free via WhatsApp.
            </p>
            <Button href="https://wa.me/1234567890" size="lg" className="bg-[#25D366] text-white hover:bg-[#128C7E] shadow-[0_0_30px_rgba(37,211,102,0.4)] border-none" icon={<MessageCircle className="w-5 h-5" data-id="element-274" />} data-id="element-273">
              Get Free Reading on WhatsApp
            </Button>
            <p className="text-sm text-gold/60 mt-6 italic tracking-wide" data-id="element-275">
              Limited time free consultation available.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Blueprint */}
      <section className="py-32 relative" data-id="element-276">
        <div className="container mx-auto px-6" data-id="element-277">
          <SectionHeader eyebrow="Your Cosmic Blueprint" title="Birth Chart Basics" description="Your birth chart is a snapshot of the sky the exact moment you were born. It is the map of your soul." data-id="element-278" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-20 max-w-6xl mx-auto" data-id="element-279">
            {[{
            icon: Sun,
            title: 'Sun Sign',
            subtitle: 'Your Core Identity',
            desc: "Represents your ego, basic personality, and conscious mind. It's the 'you' that you are learning to become in this lifetime."
          }, {
            icon: Moon,
            title: 'Moon Sign',
            subtitle: 'Your Emotional Self',
            desc: 'Rules your emotions, instincts, and subconscious. It reveals what you need to feel safe, secure, and nurtured.'
          }, {
            icon: Star,
            title: 'Rising Sign',
            subtitle: 'Your Outer Mask',
            desc: 'The sign that was rising on the eastern horizon when you were born. It dictates how others perceive you and your approach to life.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.15
          }} className="glass p-10 rounded-[2.5rem] text-center group hover:border-gold/30 transition-all duration-500" data-id="element-280">
                <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-primary to-primary-light flex items-center justify-center mb-8 shadow-lg shadow-primary/50 group-hover:scale-110 transition-transform duration-500" data-id="element-281">
                  <item.icon className="w-10 h-10 text-gold" data-id="element-282" />
                </div>
                <h3 className="text-3xl font-serif text-white mb-2" data-id="element-283">
                  {item.title}
                </h3>
                <p className="text-gold text-xs font-bold uppercase tracking-widest mb-6" data-id="element-284">
                  {item.subtitle}
                </p>
                <p className="text-beige-muted leading-relaxed" data-id="element-285">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Zodiac Wheel - Circular on Desktop */}
      <section className="py-32 bg-midnight-panel relative overflow-hidden" data-id="element-286">
        <GlowOrb color="primary" size="xl" position="top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-287" />
        <div className="container mx-auto px-6 relative z-10" data-id="element-288">
          <SectionHeader eyebrow="The Zodiac Wheel" title="Find Your Sign" data-id="element-289" />

          {/* Desktop Circular Layout */}
          <div className="hidden lg:flex relative h-[800px] items-center justify-center mt-20" data-id="element-290">
            <div className="absolute w-[600px] h-[600px] rounded-full border border-white/10 border-dashed animate-spin-slow" data-id="element-291"></div>
            <div className="absolute w-[400px] h-[400px] rounded-full border border-gold/20" data-id="element-292"></div>
            <div className="absolute w-32 h-32 rounded-full bg-primary-glow/30 blur-xl" data-id="element-293"></div>
            <Star className="w-12 h-12 text-gold absolute z-10" data-id="element-294" />

            {zodiacSigns.map((sign, i) => {
            const angle = i * 30 * (Math.PI / 180);
            const radius = 300;
            const x = Math.sin(angle) * radius;
            const y = -Math.cos(angle) * radius;
            return <motion.div key={sign.sign} initial={{
              opacity: 0,
              scale: 0
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1,
              type: 'spring'
            }} className="absolute w-32 h-32 glass rounded-full flex flex-col items-center justify-center hover:border-gold hover:scale-110 transition-all cursor-pointer z-20" style={{
              transform: `translate(${x}px, ${y}px)`
            }} data-id="element-295">
                  <div className="text-3xl text-gold mb-1" data-id="element-296">{sign.symbol}</div>
                  <h4 className="font-serif text-sm font-bold text-white" data-id="element-297">
                    {sign.sign}
                  </h4>
                  <span className="text-[9px] uppercase tracking-widest text-beige-muted" data-id="element-298">
                    {sign.element}
                  </span>
                </motion.div>;
          })}
          </div>

          {/* Mobile Grid Layout */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 lg:hidden" data-id="element-299">
            {zodiacSigns.map((sign, i) => <motion.div key={sign.sign} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.05
          }} className="glass p-6 rounded-2xl text-center hover:border-gold transition-all" data-id="element-300">
                <div className="text-4xl mb-3 text-gold" data-id="element-301">{sign.symbol}</div>
                <h4 className="font-serif text-lg font-bold text-white" data-id="element-302">
                  {sign.sign}
                </h4>
                <p className="text-xs text-beige-muted mb-2" data-id="element-303">{sign.dates}</p>
                <span className="text-[10px] uppercase tracking-widest text-gold font-bold" data-id="element-304">
                  {sign.element}
                </span>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* How it works & Checklist */}
      <section className="py-32 relative" data-id="element-305">
        <div className="container mx-auto px-6" data-id="element-306">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-6xl mx-auto" data-id="element-307">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="glass p-10 md:p-16 rounded-[3rem]" data-id="element-308">
              <h2 className="text-3xl md:text-4xl font-serif text-white mb-8" data-id="element-309">
                What You Can Ask
              </h2>
              <ul className="space-y-6" data-id="element-310">
                {['Love & Relationship Advice', 'Career Guidance & Timing', 'Financial Insight & Wealth Potential', 'Personal Horoscope Reading', 'Lucky Numbers & Auspicious Days', 'Compatibility Analysis'].map((item, i) => <li key={i} className="flex items-center gap-4" data-id="element-311">
                    <CheckCircle2 className="w-6 h-6 text-gold flex-shrink-0" data-id="element-312" />
                    <span className="text-lg text-beige" data-id="element-313">{item}</span>
                  </li>)}
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-gradient-to-br from-primary to-midnight-panel p-10 md:p-16 rounded-[3rem] border border-white/10 relative overflow-hidden" data-id="element-314">
              <div className="absolute inset-0 bg-star-dust opacity-30 mix-blend-screen" data-id="element-315"></div>
              <div className="relative z-10" data-id="element-316">
                <h2 className="text-3xl md:text-4xl font-serif text-white mb-10" data-id="element-317">
                  How It Works
                </h2>
                <div className="space-y-8" data-id="element-318">
                  {[{
                  step: '1',
                  title: 'Click WhatsApp',
                  desc: 'Start your free consultation instantly.'
                }, {
                  step: '2',
                  title: 'Send Details',
                  desc: 'Provide your Name, Birth Date, Time & Location.'
                }, {
                  step: '3',
                  title: 'Ask Question',
                  desc: 'Share what you need cosmic guidance on.'
                }, {
                  step: '4',
                  title: 'Get Insights',
                  desc: 'Receive personalized, accurate guidance.'
                }].map((item, i) => <div key={i} className="flex gap-6" data-id="element-319">
                      <div className="w-12 h-12 rounded-full bg-gold/20 text-gold font-bold text-xl flex items-center justify-center flex-shrink-0 border border-gold/30" data-id="element-320">
                        {item.step}
                      </div>
                      <div data-id="element-321">
                        <h4 className="font-serif text-xl text-white mb-1" data-id="element-322">
                          {item.title}
                        </h4>
                        <p className="text-beige-muted" data-id="element-323">{item.desc}</p>
                      </div>
                    </div>)}
                </div>
                <div className="mt-12" data-id="element-324">
                  <Button href="https://wa.me/1234567890" fullWidth size="lg" className="bg-[#25D366] text-white hover:bg-[#128C7E] border-none" icon={<MessageCircle className="w-5 h-5" data-id="element-326" />} data-id="element-325">
                    Start WhatsApp Chat
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>;
}